import { Button, Flex } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React from "react";
import {
  FormType,
  getQuestionTypeFormValue,
  removeQuestionFormValues,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/formik";
import * as Question from "src/services/formTemplate/question";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";
import { ConfigureQuestion } from "../QuestionConfiguration/ConfigureQuestion";
import { SchoolItem } from "../types";
import { AnswersOptions } from "./AnswersOptions";
import { PermissionLevel } from "./PermissionLevel";
import { QuestionConstraints, QuestionFormat } from "./QuestionContraints";
import { QuestionKey } from "./QuestionKey";
import { QuestionLink } from "./QuestionLink";
import { QuestionTitle } from "./QuestionTitle";
import { QuestionType } from "./QuestionType";

type Props = {
  sectionType: AF.SectionType;
  questionId: uuid;
  isNew: boolean;
  optionId: uuid;
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  verificationOptions: AF.FormVerification<AF.WithId>[];
};
export const AdditionalQuestion: React.FC<Props> = ({
  sectionType,
  optionId,
  questionId,
  isNew,
  schools,
  verificationOptions,
}) => {
  const form = useFormikContext<FormType>();
  const questionType = getQuestionTypeFormValue(questionId, form.values);

  const removeNewQuestion = React.useCallback(() => {
    if (
      !isNew ||
      form.values.additionalQuestions[optionId]?.questionIds === undefined
    ) {
      return;
    }

    form.setValues((values) => {
      const updatedValues: FormType = {
        ...values,
        additionalQuestions: {
          ...values.additionalQuestions,
          [optionId]: {
            ...values.additionalQuestions[optionId],
            type:
              values.additionalQuestions[optionId]?.questionIds?.length !== 1
                ? values.additionalQuestions[optionId]?.type ?? "disabled"
                : "disabled",
            questionIds:
              values.additionalQuestions[optionId]?.questionIds?.filter(
                (id) => id !== questionId
              ) ?? [],
          },
        },
      };

      return removeQuestionFormValues(updatedValues, questionId);
    });
  }, [form, isNew, optionId, questionId]);

  return (
    <Flex direction="column" gap="3">
      <Flex alignItems="flex-end" direction="row" gap="2">
        <QuestionTitle questionId={questionId} />
        <QuestionKey
          questionId={questionId}
          otherQuestions={
            [] /* we don't support question visibility on additional question */
          }
        />
        {isNew && (
          <Button
            colorScheme="gray"
            flexShrink={0}
            onClick={removeNewQuestion}
            variant="outline"
          >
            Remove question
          </Button>
        )}
      </Flex>
      <QuestionType
        questionId={questionId}
        isDisabled={isNew ? false : true}
        sectionType={sectionType}
      />
      <QuestionFormat questionId={questionId} questionType={questionType} />

      <ConfigureQuestion
        questionId={questionId}
        questionType={questionType}
        hide={["QuestionVisibility"]}
        verificationOptions={verificationOptions ?? []}
      />

      <QuestionLink questionId={questionId} />
      <PermissionLevel questionId={questionId} />
      <QuestionConstraints
        questionId={questionId}
        questionType={questionType}
      />
      {Question.hasOptions({ type: questionType }) && (
        <AnswersOptions
          sectionType={sectionType}
          questionId={questionId}
          schools={schools}
          showAdditionalQuestions={false}
          verificationOptions={verificationOptions}
          question={undefined}
          otherQuestions={[]}
        />
      )}
    </Flex>
  );
};
